import React, { useEffect, useRef, useState, useMemo } from "react";
import { Grid, Typography, Button, Tooltip, Chip, Box, Breadcrumbs, Link, IconButton } from "@mui/material";
import { AlertMessage } from "../../../components/AlertMessage";

//layout import
import Layout from "../../../layout/Layout";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import * as Yup from "yup";
import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate, useLocation } from "react-router-dom";
import DoneIcon from "@mui/icons-material/Done";
import DoneAllIcon from "@mui/icons-material/DoneAll";
import { MaterialReactTable } from "material-react-table";
import dayjs from "dayjs";
import axios from "axios";
import { config, leaveAPI } from "../../../config";

function ApproveLeave() {
  const navigate = useNavigate();
  const location = useLocation();
  const childRef = useRef();

  const validationSchema = Yup.object().shape({
    txtLeaveReason: Yup.string().required("Leave Reason is required"),
    txtLeaveSubject: Yup.string().required("Leave Subject is required"),
  });
  const formOptions = { resolver: yupResolver(validationSchema) };
  const { register, handleSubmit, reset, formState, setValue } =
    useForm(formOptions);

  const [rawLeaveList, setRawLeaveList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("all");

  // Read initial filter from dashboard state if present
  useEffect(() => {
    if (location.state?.status !== undefined) {
      const stateStatus = location.state.status;
      if (stateStatus == 84 || stateStatus == 85 || stateStatus == 86) {
        setSelectedStatus(Number(stateStatus));
      } else if (stateStatus === "all") {
        setSelectedStatus("all");
      }
    }
  }, [location.state?.status]);

  const fetchUsers = async () => {
    setLoading(true);
    // Request all leaves (pageSize 10000) so we can filter client-side
    const data = JSON.stringify({
      pageSize: 10000,
      pageIndex: 0,
      intType: 8,
      columnFilters: [],
      sorting: [],
    });

    config.method = "POST";
    config.data = data;
    config.url = leaveAPI;
    axios(config)
      .then(function (response) {
        setRawLeaveList(response.data.result || []);
      })
      .catch(function (error) {
        console.log(error);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleLeave = (LeaveID, status) => {
    const data = JSON.stringify({
      intType: 3,
      intLeaveID: LeaveID,
      intLeaveStatus: status,
    });

    config.method = "POST";
    config.data = data;
    config.url = leaveAPI;

    axios(config)
      .then(function (response) {
        if (childRef.current) {
          childRef.current.triggerAlert(
            JSON.stringify({
              message: response.data.message || "Leave status updated successfully",
              messageType: "success",
            })
          );
        }
        setTimeout(() => {
          fetchUsers();
        }, 1000);
      })
      .catch(function (error) {
        console.log(error);
        if (childRef.current) {
          childRef.current.triggerAlert(
            JSON.stringify({
              message: "Something went wrong",
              messageType: "error",
            })
          );
        }
      });
  };

  // Company-filtered leaves list
  const companyLeaves = useMemo(() => {
    return rawLeaveList.filter((leave) => {
      if (location.state?.companyName) {
        if (leave.company_name !== location.state.companyName) return false;
      }
      if (location.state?.month && location.state?.year) {
        const fromDate = dayjs(leave.leave_from_date);
        const m = fromDate.month() + 1;
        const y = fromDate.year();
        if (m !== location.state.month || y !== location.state.year) return false;
      }
      return true;
    });
  }, [rawLeaveList, location.state?.companyName, location.state?.month, location.state?.year]);

  // Filter leaves based on selectedStatus
  const filteredLeaveList = useMemo(() => {
    return companyLeaves.filter((leave) => {
      if (selectedStatus === "all") return true;
      return Number(leave.leave_status) === Number(selectedStatus);
    });
  }, [companyLeaves, selectedStatus]);

  const columns = useMemo(
    () => [
      {
        header: "S.No.",
        Cell: ({ row, table }) => {
          const pageIndex = table.getState().pagination.pageIndex;
          const pageSize = table.getState().pagination.pageSize;
          return pageIndex * pageSize + row.index + 1;
        },
        size: 60,
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "employee_name",
        header: "Employee Name",
        width: "30%",
        Cell: ({ row }) => {
          const name = row.original.employee_name;
          const employeeNo = row.original.employee_no;
          return <span>{`${name} (${employeeNo})`}</span>;
        },
        enableSorting: false,
      },
      {
        accessorKey: "leave_from_date",
        header: "From Date",
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          return dayjs(rawDate).format("DD-MM-YYYY");
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "leave_to_date",
        header: "To Date",
        Cell: ({ cell }) => {
          const rawDate = cell.getValue();
          return dayjs(rawDate).format("DD-MM-YYYY");
        },
        enableColumnFilter: false,
      },
      {
        accessorKey: "leave_subject",
        header: "Subject",
        Cell: ({ cell }) => (
          <Tooltip title={cell.getValue()} arrow>
            <span>{cell.getValue()}</span>
          </Tooltip>
        ),
        enableSorting: false,
        enableColumnFilter: false,
      },
      {
        accessorKey: "action",
        header: "Action",
        enableSorting: false,
        Cell: ({ row }) => {
          const rowData = row.original;
          return (
            <>
              {rowData.leave_status == 84 ? (
                <>
                  <Tooltip title="Accept Leave">
                    <IconButton
                      color="success"
                      onClick={() => handleLeave(rowData.id, 85)}
                    >
                      <DoneIcon />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Decline Leave">
                    <IconButton
                      color="error"
                      onClick={() => handleLeave(rowData.id, 86)}
                    >
                      <CloseIcon />
                    </IconButton>
                  </Tooltip>
                </>
              ) : rowData.leave_status == 85 ? (
                <Tooltip title="Leave Accepted">
                  <DoneAllIcon color="success" />
                </Tooltip>
              ) : (
                <Tooltip title="Leave Denied">
                  <CloseIcon color="error" />
                </Tooltip>
              )}
            </>
          );
        },
        enableSorting: false,
        enableColumnFilter: false,
      },
    ],
    []
  );

  return (
    <Layout>
      <Grid className="employee-form bold-header">
        <Grid container spacing={3} sx={{ mb: 2 }}>
          <Grid
            textAlign="center"
            item
            className="form-lable"
            xs={12}
            lg={6}
            md={6}
          >
            <Typography className="page_title" textAlign="left">
              Leave Approval
            </Typography>
            <Breadcrumbs aria-label="breadcrumb">
              <Link underline="hover" color="inherit" href="/dashboard">
                Dashboard
              </Link>
              <Typography color="text.primary">Leave</Typography>
            </Breadcrumbs>
          </Grid>
        </Grid>

        {/* STATUS SHIELD FILTERS */}
        <Box sx={{ display: "flex", gap: 1.5, mb: 3, flexWrap: "wrap" }}>
          <Chip
            label={`All Leaves (${companyLeaves.length})`}
            onClick={() => setSelectedStatus("all")}
            color={selectedStatus === "all" ? "primary" : "default"}
            variant={selectedStatus === "all" ? "filled" : "outlined"}
            clickable
            sx={{ fontWeight: "bold" }}
          />
          <Chip
            label={`Pending Review (${companyLeaves.filter((l) => Number(l.leave_status) === 84).length})`}
            onClick={() => setSelectedStatus(84)}
            color="warning"
            variant={selectedStatus === 84 ? "filled" : "outlined"}
            clickable
            sx={{ fontWeight: "bold" }}
          />
          <Chip
            label={`Approved (${companyLeaves.filter((l) => Number(l.leave_status) === 85).length})`}
            onClick={() => setSelectedStatus(85)}
            color="success"
            variant={selectedStatus === 85 ? "filled" : "outlined"}
            clickable
            sx={{ fontWeight: "bold" }}
          />
          <Chip
            label={`Rejected (${companyLeaves.filter((l) => Number(l.leave_status) === 86).length})`}
            onClick={() => setSelectedStatus(86)}
            color="error"
            variant={selectedStatus === 86 ? "filled" : "outlined"}
            clickable
            sx={{ fontWeight: "bold" }}
          />
        </Box>

        <MaterialReactTable
          columns={columns}
          data={filteredLeaveList}
          enableGlobalFilter={false}
          enableFullScreenToggle={false}
          enableDensityToggle={false}
          className="mat-react-tbl"
          state={{
            isLoading: loading,
          }}
          muiTableBodyCellProps={{
            sx: {
              padding: "3px 8px",
              border: "1px solid #ccc",
            },
          }}
          muiTableHeadCellProps={{
            sx: {
              "& input": {
                color: "#fff !important",
                "&::placeholder": {
                  color: "#fff !important",
                  opacity: 1,
                },
              },
              "& .MuiSelect-select": {
                color: "#fff !important",
              },
              "& .MuiSelect-icon": {
                color: "#fff !important",
              },
              color: "#fff",
              fontWeight: "bold",
              "& svg": {
                color: "#fff",
              },
              "& .MuiTableSortLabel-root": {
                color: "#495057",
                "&.Mui-active": {
                  color: "#fff",
                  "& .MuiTableSortLabel-icon": {
                    color: "#fff !important",
                  },
                },
              },
              "& .MuiTableSortLabel-icon": {
                color: "#fff !important",
              },
            },
          }}
          muiTableBodyRowProps={({ row }) => ({
            sx: {
              borderBottom: "1px solid #ccc",
              backgroundColor: row.index % 2 === 0 ? "#ffffff" : "#e3f2fd",
            },
          })}
          muiTableHeadCellFilterTextFieldProps={{
            sx: {
              "& input": {
                color: "#fff !important",
              },
              "& .MuiInput-underline:before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:hover:not(.Mui-disabled):before": {
                borderBottomColor: "#fff !important",
              },
              "& .MuiInput-underline:after": {
                borderBottomColor: "#fff !important",
              },
            },
          }}
        />
      </Grid>
      <AlertMessage ref={childRef} />
    </Layout>
  );
}

export default ApproveLeave;
